import { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { CHART_CATEGORICAL, FONT_SANS, TOKENS } from 'Design/tokens'
import { Graph } from 'Common/Graph/Graph'
import { Panel } from 'Common/Components/Panel'
import { SegmentedControl } from 'Common/Components/SegmentedControl'
import { methodLabel } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { TemplateSummaryOutputs } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'

type Grouping = 'skuGroup' | 'batch'
type Measure = 'count' | 'volume'

const CYCLES = ['1', '2', '4', '8', 'FOQ'] as const

const valueFor = (
  summary: Partial<TemplateSummaryOutputs>,
  cycle: (typeof CYCLES)[number],
  grouping: Grouping,
  measure: Measure
) => {
  const prefix = cycle === 'FOQ' ? 'FOQ' : `Cycle${cycle}`
  const suffix =
    grouping === 'skuGroup'
      ? measure === 'count'
        ? 'CountSkuGroup'
        : 'SkuGroupVolume'
      : measure === 'count'
        ? 'CountBatch'
        : 'BatchVolume'
  const value = summary[`${prefix}_${suffix}` as keyof TemplateSummaryOutputs]
  return typeof value === 'number' ? value : 0
}

/**
 * How each method splits its work centre across cycles (1, 2, 4, 8 weeks,
 * FOQ) as one grouped bar chart, a bar per method in each cycle, by SKU group
 * or batch group and as a count or a volume. The legend carries each method's
 * volume-weighted cycle speed. Replaces the Results tab's one-method chart.
 */
export const MethodCycleSpeed = ({
  methods,
}: {
  /** The methods that have finished, each with its summary row. */
  methods: {
    templateMethod: string
    summary: Partial<TemplateSummaryOutputs>
  }[]
}) => {
  const [grouping, setGrouping] = useState<Grouping>('skuGroup')
  const [measure, setMeasure] = useState<Measure>('count')

  const data = useMemo(
    () =>
      methods.map(({ templateMethod, summary }, i) => ({
        type: 'bar',
        name: `${methodLabel(templateMethod)} · speed ${
          typeof summary.avgCycleSpeed === 'number'
            ? summary.avgCycleSpeed.toFixed(2)
            : '—'
        }`,
        x: CYCLES.map((c) => (c === 'FOQ' ? 'FOQ' : `${c} wk`)),
        y: CYCLES.map((c) => valueFor(summary, c, grouping, measure)),
        marker: {
          color:
            CHART_CATEGORICAL[i % CHART_CATEGORICAL.length] ?? TOKENS.chartCat1,
        },
        hovertemplate: `<b>${methodLabel(templateMethod)}</b><br>Cycle %{x}: %{y:,}<extra></extra>`,
      })),
    [methods, grouping, measure]
  )

  const layout = useMemo(() => {
    const axis = {
      showline: true,
      linewidth: 1,
      linecolor: TOKENS.chartGrid,
      tickfont: { family: FONT_SANS, size: 11, color: TOKENS.chartInk },
    }
    return {
      height: 260,
      barmode: 'group',
      bargap: 0.25,
      margin: { l: 56, r: 8, b: 32, t: 8 },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      legend: {
        orientation: 'h',
        y: -0.18,
        font: { family: FONT_SANS, size: 11, color: TOKENS.chartInk },
      },
      hoverlabel: {
        bordercolor: TOKENS.line300,
        bgcolor: TOKENS.surface0,
        font: { color: TOKENS.chartInk, family: FONT_SANS },
      },
      xaxis: { ...axis, type: 'category' },
      yaxis: {
        ...axis,
        showgrid: true,
        gridcolor: TOKENS.chartGrid,
        zeroline: false,
        title: {
          text: measure === 'count' ? 'Count' : 'Volume',
          font: { family: FONT_SANS, size: 11, color: TOKENS.chartInk },
        },
      },
    }
  }, [measure])

  return (
    <Panel
      title="Cycle speed"
      action={
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <SegmentedControl
            label="Group cycles by"
            value={grouping}
            onChange={setGrouping}
            options={[
              { value: 'skuGroup', label: 'SKU group' },
              { value: 'batch', label: 'Batch group' },
            ]}
          />
          <SegmentedControl
            label="Measure"
            value={measure}
            onChange={setMeasure}
            options={[
              { value: 'count', label: 'Count' },
              { value: 'volume', label: 'Volume' },
            ]}
          />
        </Box>
      }
    >
      {methods.length === 0 ? (
        <Box
          sx={{
            py: '32px',
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--ink-500)',
          }}
        >
          No method has finished yet.
        </Box>
      ) : (
        <Graph data={data} layout={layout} config={{ displayModeBar: false }} />
      )}
    </Panel>
  )
}
